// =============================================================================
// useCanOpenPieceRates — would the /piecework-config route let this person in?
//
// F11 UF-12b: the packhouse rates page admits a Config holder (as before) OR a
// labour manager. The labour-manager arm matches the packhouse arms of
// set_piece_rate's gate (mig 20261016190000): it admits can_manage_labour() OR
// has_perm('harvest_rates','manage'), and canManageLabourFrom() restates the
// first — the harvest_rates arm is a harvest key a packhouse role does not
// use. So a custom role holding labour:manage but not Config is no longer
// bounced to Home off a page whose save the server would accept. The arm also
// needs the Labour module the route gate's hasModule would grant (the
// tenant's entitlement cap and the role's view), and a PACKHOUSE tenant, like
// every gPack route.
//
// ONE answer for the route guard (App.tsx gPieceRates) and every pointer to
// the page (PieceRatesLink), so a link is offered exactly when the route would
// open. null while any read is still deciding — the route gate's isPending
// rule (finding H4): no link, no render, never a bounce on an interim value.
// =============================================================================

import { useCanOpenPackhouseConfig } from '@/hooks/useCanOpenPackhouseConfig'
import { useCanOpenModule } from '@/hooks/useCanOpenModule'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { usePermissions } from '@/lib/usePermissions'
import type { MyPermissions } from '@/lib/permissions'
import { canManageLabourFrom } from '@/modules/shared/labour/useCanManageLabour'

/** The rule, pure. `canOpenConfig` already carries the PACKHOUSE-kind half. */
export function pieceRatesRouteAdmits(a: {
  canOpenConfig: boolean
  canOpenLabour: boolean
  tenantKind:    string | null | undefined
  perms:         MyPermissions | null | undefined
}): boolean {
  if (a.canOpenConfig) return true
  return a.tenantKind === 'PACKHOUSE' && a.canOpenLabour && canManageLabourFrom(a.perms)
}

export function useCanOpenPieceRates(): boolean | null {
  const canOpenConfig = useCanOpenPackhouseConfig()
  const canOpenLabour = useCanOpenModule('labour')
  const { data: tenant, isPending: tenantPending } = useActiveTenant()
  const { perms, isPending: permsPending } = usePermissions()
  if (canOpenConfig === null || canOpenLabour === null || tenantPending || permsPending) return null
  return pieceRatesRouteAdmits({ canOpenConfig, canOpenLabour, tenantKind: tenant?.kind, perms })
}
